import type { ContributorDto, ContributorRole, RoleDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { EventDto } from '@/types/dtos/events/response/event.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { TFunction } from 'i18next';
import { getRoleSlotDescription } from '@/components/events/helpers/get-role-slot-description.helper';
import { getEntityName } from '@/components/events/helpers/get-entity-name.helper';
import { filterSlotsForStagingInviteBuild } from '@/components/events/detail/tabs/contributors/staging-invite-build.helpers';
import type { InvitePublicationUiContext } from '@/utils/contributor-invite-publication.utils';

export type InvitePayloadRow = {
  contributorId: string;
  roles: { roleId: string; slotIds: string[] }[];
};

export type InvitePreviewRoleLine = {
  roleHeadline: string;
  /** Only `stagedForInvite` slots in flow context; empty means role-only invite. */
  slotLines: string[];
};

export type InvitePreviewContributor = {
  contributorId: string;
  displayName: string;
  roles: InvitePreviewRoleLine[];
};

/** `getRoleSlotDescription` prefixes FlowSlot lines with "in "; omit for the invite preview list. */
function formatInvitePreviewSlotLine(raw: string): string {
  return raw.startsWith('in ') ? raw.slice(3) : raw;
}

function formatRoleHeadline(role: RoleDto<ContributorRole>, t: TFunction): string {
  const entityName = getEntityName({ entity: role.entity, entityType: role.entityType });
  if (role.entityType === 'Event') {
    return t(`common.roleLabels.${role.role}`);
  }
  return `${entityName} (${t(`common.roleLabels.${role.role}`)})`;
}

/**
 * Confirmation modal preview: only roles with `role.stagedForInvite` and/or visible slots with
 * `slot.stagedForInvite` (contributor-level flag is not used — it is true whenever any child is staged).
 * Contributor order follows `contributorIdsInBatch` (e.g. invite payload order).
 */
export function buildInviteModalPreview(
  contributors: ContributorDto[],
  flowContext: FlowDto | undefined,
  contributorIdsInBatch: string[],
  eventForSlots: EventDto | undefined,
  eventFlows: FlowDto[] | undefined,
  t: TFunction,
  invitePublicationCtx?: InvitePublicationUiContext,
): InvitePreviewContributor[] {
  const byId = new Map(contributors.map((c) => [c.id, c]));
  const out: InvitePreviewContributor[] = [];

  for (const contributorId of contributorIdsInBatch) {
    const c = byId.get(contributorId);
    if (!c) continue;

    const displayName = c.profiles?.[0]?.displayName?.trim() || c.email || c.id;
    const roleLines: InvitePreviewRoleLine[] = [];

    for (const role of c.roles) {
      if ((role as { deleted?: boolean }).deleted) continue;

      const visibleSlots = filterSlotsForStagingInviteBuild(role.slots, flowContext, role, invitePublicationCtx);
      const stagedSlots = visibleSlots.filter((s) => s.stagedForInvite === true && !(!!s.invitedAt && s.acceptedAt));
      const roleLevelStagedNeedsInvite = role.stagedForInvite === true && !(!!role.invitedAt && role.acceptedAt);

      // Do not use contributor.stagedForInvite here — it mirrors “any child staged” and would list every role.
      const includeRole = roleLevelStagedNeedsInvite || stagedSlots.length > 0;
      if (!includeRole) continue;

      const headline = formatRoleHeadline(role, t);

      if (stagedSlots.length > 0) {
        const slotLines = stagedSlots.map((roleSlot) => {
          const desc = getRoleSlotDescription(roleSlot, eventForSlots, eventFlows);
          return formatInvitePreviewSlotLine(desc ?? roleSlot.id);
        });
        roleLines.push({ roleHeadline: headline, slotLines });
      } else {
        roleLines.push({ roleHeadline: headline, slotLines: [] });
      }
    }

    if (roleLines.length > 0) {
      out.push({ contributorId: c.id, displayName, roles: roleLines });
    }
  }

  return out;
}
